import * as i0 from '@angular/core';
import { inject, ChangeDetectorRef, Component, ChangeDetectionStrategy, ViewChild, HostListener, ApplicationRef, Injector, EnvironmentInjector, createComponent, TemplateRef, Injectable, NgModule } from '@angular/core';
import { SiTranslateService, SiNoTranslateService } from '@simpl/element-ng/translate';
import { Subject, ReplaySubject } from 'rxjs';
import * as i1 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';

/**
 * Reference to a modal dialog
 * @type T: the type of the content
 * @type CT: the close type
 */
class ModalRef {
    hidden = new Subject();
    shown = new ReplaySubject(1);
    data = {};
    ignoreBackdropClick = true;
    dialogClass = '';
    layer = 0;
    closeValue;
    get content() {
        return undefined;
    }
    isCurrent = () => false;
    detach = () => { };
    hide = () => { };
}

class SiModalComponent {
    modalRef = inject((ModalRef));
    changeDetectorRef = inject(ChangeDetectorRef);
    dialogClass = this.modalRef.dialogClass ?? '';
    titleId = this.modalRef.data?.ariaLabelledBy ?? '';
    init = false;
    show = false;
    showBackdropClass;
    clickStartInDialog = false;
    origBodyOverflow;
    showTimer;
    backdropTimer;
    backdropGhostClickPrevention = true;
    modalContainerRef;
    ngOnInit() {
        setTimeout(() => (this.backdropGhostClickPrevention = false), this.animationTime(300));
        this.init = true;
        this.showTimer = setTimeout(() => {
            this.show = true;
            this.changeDetectorRef.markForCheck();
        }, this.animationTime(150));
    }
    ngAfterViewInit() {
        this.modalContainerRef.nativeElement.querySelector('.modal-dialog').focus();
        queueMicrotask(() => this.modalRef?.shown.next(this.modalContainerRef));
    }
    ngOnDestroy() {
        this.hideBackdrop();
    }
    hideDialog(param) {
        clearTimeout(this.showTimer);
        if (!this.show) {
            return;
        }
        this.show = false;
        setTimeout(() => {
            this.hideBackdrop();
            this.changeDetectorRef.markForCheck();
            setTimeout(() => this.modalRef?.detach(), this.animationTime(150));
        }, this.animationTime(300));
        this.modalRef?.hidden.next(param);
        this.modalRef?.hidden.complete();
        this.changeDetectorRef.markForCheck();
    }
    showBackdrop() {
        if (this.modalRef?.data.animated !== false) {
            this.showBackdropClass = false;
            this.backdropTimer = setTimeout(() => {
                this.showBackdropClass = true;
                this.changeDetectorRef.markForCheck();
            }, 16);
        }
        else {
            this.showBackdropClass = true;
        }
        this.origBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.changeDetectorRef.markForCheck();
    }
    hideBackdrop() {
        clearTimeout(this.backdropTimer);
        if (this.showBackdropClass !== undefined) {
            this.showBackdropClass = false;
        }
        if (this.origBodyOverflow !== undefined) {
            document.body.style.overflow = this.origBodyOverflow;
            this.origBodyOverflow = undefined;
        }
    }
    clickStarted(event) {
        this.clickStartInDialog = event.target !== this.modalContainerRef.nativeElement;
    }
    onClickStop(event) {
        const clickedInBackdrop = event.target === this.modalContainerRef.nativeElement && !this.clickStartInDialog;
        if (this.modalRef?.ignoreBackdropClick || !clickedInBackdrop) {
            this.clickStartInDialog = false;
            return;
        }
        if (!this.backdropGhostClickPrevention) {
            // Called when backdrop close is allowed and user clicks on the backdrop
            this.hideDialog(this.modalRef.closeValue);
        }
        else {
            // When in ghost click prevention mode, avoid text selection
            document.getSelection()?.removeAllRanges();
        }
    }
    onEsc(event) {
        if (this.modalRef?.data.keyboard && this.modalRef?.isCurrent()) {
            event.preventDefault();
            this.hideDialog(this.modalRef.closeValue);
        }
    }
    animationTime(millis) {
        return this.modalRef?.data.animated !== false ? millis : 0;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiModalComponent, selector: "si-modal", host: { listeners: { "mousedown": "clickStarted($event)", "mouseup": "onClickStop($event)", "window:keydown.esc": "onEsc($event)" } }, viewQueries: [{ propertyName: "modalContainerRef", first: true, predicate: ["modalContainer"], descendants: true }], ngImport: i0, template: "<div\n  *ngIf=\"showBackdropClass !== undefined\"\n  class=\"modal-backdrop\"\n  [class.fade]=\"modalRef.data.animated !== false\"\n  [class.show]=\"showBackdropClass\"\n></div>\n<div\n  *ngIf=\"init\"\n  #modalContainer\n  role=\"dialog\"\n  class=\"modal d-block\"\n  aria-modal=\"true\"\n  [class.fade]=\"modalRef.data.animated !== false\"\n  [class.show]=\"show\"\n  [attr.aria-labelledby]=\"titleId\"\n>\n  <div class=\"modal-dialog\" cdkTrapFocus tabindex=\"-1\" [ngClass]=\"dialogClass\">\n    <div class=\"modal-content\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-modal', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  *ngIf=\"showBackdropClass !== undefined\"\n  class=\"modal-backdrop\"\n  [class.fade]=\"modalRef.data.animated !== false\"\n  [class.show]=\"showBackdropClass\"\n></div>\n<div\n  *ngIf=\"init\"\n  #modalContainer\n  role=\"dialog\"\n  class=\"modal d-block\"\n  aria-modal=\"true\"\n  [class.fade]=\"modalRef.data.animated !== false\"\n  [class.show]=\"show\"\n  [attr.aria-labelledby]=\"titleId\"\n>\n  <div class=\"modal-dialog\" cdkTrapFocus tabindex=\"-1\" [ngClass]=\"dialogClass\">\n    <div class=\"modal-content\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n" }]
        }], propDecorators: { modalContainerRef: [{
                type: ViewChild,
                args: ['modalContainer']
            }], clickStarted: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onClickStop: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onEsc: [{
                type: HostListener,
                args: ['window:keydown.esc', ['$event']]
            }] } });

class ModalRefImpl extends ModalRef {
    contentRef;
    viewRef;
    modalCompRef;
    componentRef;
    previousFocusElement;
    get content() {
        return this.componentRef?.instance;
    }
    constructor(contentRef, layer, data, previousFocusElement, closeValue) {
        super();
        this.contentRef = contentRef;
        this.layer = layer;
        this.data = data;
        this.ignoreBackdropClick = data.ignoreBackdropClick !== false;
        this.dialogClass = data?.class ?? '';
        this.previousFocusElement = previousFocusElement;
        this.closeValue = closeValue;
    }
}
class SiModalService {
    modalsCount = 0;
    appRef = inject(ApplicationRef);
    injector = inject(Injector);
    environmentInjector = inject(EnvironmentInjector);
    show(content, config, closeValue) {
        const modalRef = new ModalRefImpl(content, ++this.modalsCount, config, document.activeElement, closeValue);
        const siModalRef = this.attachComponent(modalRef);
        modalRef.modalCompRef = siModalRef;
        modalRef.detach = () => this.detach(modalRef);
        modalRef.hide = (param) => siModalRef.instance.hideDialog(param);
        modalRef.isCurrent = () => modalRef.layer === this.modalsCount;
        if (this.modalsCount === 1) {
            siModalRef.instance.showBackdrop();
        }
        return modalRef;
    }
    attachComponent(modalRef) {
        const providers = [{ provide: ModalRef, useValue: modalRef }];
        modalRef.data.providers?.forEach(p => providers.push(p));
        const injector = Injector.create({
            providers,
            parent: modalRef.data.injector ?? this.buildInjector()
        });
        const componentRef = createComponent(SiModalComponent, {
            environmentInjector: this.environmentInjector,
            elementInjector: injector,
            projectableNodes: this.getContentProjectableNodes(injector, modalRef)
        });
        document.body.appendChild(componentRef.location.nativeElement);
        this.appRef.attachView(componentRef.hostView);
        return componentRef;
    }
    getContentProjectableNodes(injector, modalRef) {
        if (modalRef.contentRef instanceof TemplateRef) {
            modalRef.viewRef = modalRef.contentRef.createEmbeddedView({
                modalRef,
                ...modalRef.data.initialState
            });
            this.appRef.attachView(modalRef.viewRef);
            return [modalRef.viewRef.rootNodes];
        }
        modalRef.componentRef = createComponent(modalRef.contentRef, {
            environmentInjector: this.environmentInjector,
            elementInjector: injector
        });
        Object.assign(modalRef.componentRef.instance, modalRef.data?.initialState); // set initial @Input()s
        modalRef.componentRef.changeDetectorRef.detectChanges();
        this.appRef.attachView(modalRef.componentRef.hostView);
        return [[modalRef.componentRef.location.nativeElement]];
    }
    detach(modalRef) {
        const ref = modalRef;
        if (ref.modalCompRef) {
            ref.modalCompRef?.destroy();
            ref.modalCompRef = undefined;
            ref.componentRef?.destroy();
            ref.componentRef = undefined;
            ref.viewRef?.destroy();
            ref.viewRef = undefined;
            ref.shown.complete();
            this.modalsCount--;
        }
        if (ref.previousFocusElement?.focus) {
            ref.previousFocusElement.focus();
            ref.previousFocusElement = null;
        }
    }
    // TODO remove once translation must be defined at application start
    // Notification service is provided in 'root'. If no translation is defined, SiNoTranslateService is not provided
    buildInjector() {
        let injector = this.injector;
        if (!injector.get(SiTranslateService, null)) {
            injector = Injector.create({
                providers: [{ provide: SiTranslateService, useClass: SiNoTranslateService, deps: [] }],
                parent: this.injector
            });
        }
        return injector;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class SiModalModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiModalModule, declarations: [SiModalComponent], imports: [A11yModule, CommonModule] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalModule, imports: [A11yModule, CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [A11yModule, CommonModule],
                    declarations: [SiModalComponent]
                }]
        }] });

let idCounter = 1;
const createModalConfig = (inputs) => {
    if (inputs) {
        Object.keys(inputs).forEach(k => inputs[k] === undefined && delete inputs[k]);
    }
    else {
        inputs = {};
    }
    const config = {
        initialState: inputs,
        ignoreBackdropClick: true,
        keyboard: false,
        animated: true,
        class: 'modal-dialog-centered'
    };
    const id = `__si-modal-id-${idCounter++}`;
    config.initialState.titleId = id;
    config.ariaLabelledBy = id;
    return config;
};

/**
 * Generated bundle index. Do not edit.
 */

export { ModalRef, SiModalComponent, SiModalModule, SiModalService, createModalConfig };
